import React, { createContext, useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { logoutService } from "../services/authService";
import { ROUTES } from "../constants";
import { sendOTP } from "../services/verifyEmailService";
import { identifyUser, resetAnalytics } from "../utilities/analyticsService";
import { disconnectSocket } from "../services/socket";

// Create the AuthContext
const AuthContext = createContext();

// Custom hook for using the AuthContext
export const useAuth = () => useContext(AuthContext);

const PERMISSION_ALIASES = {
    "app_settings.manage_settings": ["app_settings.manage_default_values"],
    "app_settings.manage_order_settings": ["app_settings.manage_default_values"],
    "app_settings.manage_inventory_settings": ["app_settings.manage_default_values"],
    "app_settings.manage_site_settings": ["app_settings.manage_default_values"],
    "app_settings.manage_cart_retention": ["app_settings.manage_default_values"],
    "app_settings.manage_reminder_settings": ["app_settings.manage_default_values"],
    "app_settings.manage_line_discounts": ["app_settings.manage_promotions"],
    "line_discount_management.view_line_discounts": ["app_settings.manage_line_discounts", "app_settings.manage_promotions"],
    "line_discount_management.create_line_discounts": ["app_settings.manage_line_discounts", "app_settings.manage_promotions"],
    "line_discount_management.edit_line_discounts": ["app_settings.manage_line_discounts", "app_settings.manage_promotions"],
    "line_discount_management.delete_line_discounts": ["app_settings.manage_line_discounts", "app_settings.manage_promotions"],
    "promotion_management.view_promotions": ["app_settings.manage_promotions"],
    "promotion_management.create_promotions": ["app_settings.manage_promotions"],
    "promotion_management.edit_promotions": ["app_settings.manage_promotions"],
    "promotion_management.delete_promotions": ["app_settings.manage_promotions"],
    "app_settings.access_chatbot": ["profile.access_chatbot"],
    "profile.access_chatbot": ["app_settings.access_chatbot"],
    "analytics.view_order_analytics": ["analytics"],
    "analytics.view_user_analytics": ["analytics"],
};

const normalizePermissionsObject = (permissions) => {
    if (!permissions) return {};
    if (typeof permissions === "string") {
        try {
            const parsed = JSON.parse(permissions);
            return parsed && typeof parsed === "object" ? parsed : {};
        } catch (_) {
            return {};
        }
    }
    return permissions && typeof permissions === "object" ? permissions : {};
};

const normalizeBoolean = (value, fallback = false) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (normalized === "true" || normalized === "1" || normalized === "yes") return true;
        if (normalized === "false" || normalized === "0" || normalized === "no") return false;
    }
    return fallback;
};

const normalizeAuthBooleans = (user = {}) => ({
    ...user,
    first_login: normalizeBoolean(user?.first_login, false),
    email_verified: normalizeBoolean(user?.email_verified, false),
    eula_accepted: normalizeBoolean(user?.eula_accepted, false),
    cookie_policy_accepted: normalizeBoolean(user?.cookie_policy_accepted, false),
});

const getAnalyticsUserPayload = (user = {}) => ({
    profileId: user.user_id,
    user_id: user.user_id,
    user_name: user.user_display_name || user.user_name || "",
    user_email: user.user_email || "",
    customer_id: user.customer_id || "",
    customer_name: user.customer_name || "",
    site_id: user.site_id || "",
    site_name: user.site_name || "",
    location: user.location || user.region || user.country || "",
    user_role: user.role?.role_name || user.user_role_name || user.user_role || "",
});

const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [needsEulaAcceptance, setNeedsEulaAcceptance] = useState(false);
    const [needsEmailVerification, setNeedsEmailVerification] = useState(false);
    const navigate = useNavigate();

    // Load session data on initial mount (WITHOUT server validation for now)
    useEffect(() => {
        
        
        const token = localStorage.getItem("token");
        const userData = localStorage.getItem("user");
        const sessionExpiration = localStorage.getItem("sessionExpiration");

        // Check if session has expired
        if (sessionExpiration && Date.now() > Number(sessionExpiration)) {
            
            const path = `${window.location.pathname}${window.location.search}${window.location.hash}` || '/';
            try {
                localStorage.clear();
                localStorage.setItem('sessionExpired', '1');
                localStorage.setItem('postLoginRedirect', path);
            } catch {}
            setUser(null);
            setLoading(false);
            // Notify global overlay
            try { window.dispatchEvent(new Event('session-expired')); } catch {}
            return;
        }

        if (token && userData) {
            try {
                const parsedUser = JSON.parse(userData);
                const normalizedUser = normalizeAuthBooleans(parsedUser);
                
                setUser(normalizedUser);
            } catch (error) {
                console.error("Error parsing user data:", error);
                localStorage.clear();
                setUser(null);
            }
        } else {
            
            setUser(null);
        }
        
        setLoading(false);
    }, []);

    const sendOtp = async (user_id) => {
        
        try {
            const response = await sendOTP(user_id);
        } catch (error) {
            console.error("Error sending OTP:", error);
        }
    };

    // Soft-expire the session: clear auth but keep redirect flags
    const expireSession = React.useCallback(() => {
        try {
            const path = `${window.location.pathname}${window.location.search}${window.location.hash}` || '/';
            localStorage.setItem('postLoginRedirect', localStorage.getItem('postLoginRedirect') || path);
            localStorage.setItem('sessionExpired', '1');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('sessionExpiration');
        } catch {}
        disconnectSocket();
        setUser(null);
    }, []);

    // Simplified login without server token validation
    const login = async (token, userData, user_meta_data, role) => {
        
        
        try {
            disconnectSocket();
            const expirationTime = Date.now() + 8 * 60 * 60 * 1000; // 8 hours from now
            const completeUserData = normalizeAuthBooleans({ ...userData, ...user_meta_data, role });
            
            // Set user data directly (no server validation for now)
            setUser(completeUserData);
            localStorage.setItem("token", token);
            localStorage.setItem("user", JSON.stringify(completeUserData));
            localStorage.setItem("sessionExpiration", expirationTime);

            console.log("Login successful, user data:", completeUserData);
            const redirect = localStorage.getItem("postLoginRedirect");
            if (localStorage.getItem('sessionExpired')) localStorage.removeItem('sessionExpired');
            if (redirect && redirect !== ROUTES.LOGIN && redirect !== ROUTES.LOGOUT) {
                try { localStorage.removeItem('postLoginRedirect'); } catch {}
                navigate(redirect);
            } else {
                navigate(ROUTES.HOME_ALIAS);
            }
        } catch (error) {
            console.error("Login failed:", error);
            localStorage.clear();
            setUser(null);
        }
    };

    const logout = async () => {
        
        try {
            await logoutService();
        } catch (e) {
            // ignore, still clear client state
        }

        disconnectSocket();
        resetAnalytics();
        localStorage.clear();
        setUser(null);
        navigate(ROUTES.LOGIN);
    };

    const checkSessionExpiration = () => {
        const expirationTime = localStorage.getItem("sessionExpiration");
        if (expirationTime && Date.now() > Number(expirationTime)) {
            
            try {
                const path = `${window.location.pathname}${window.location.search}${window.location.hash}` || '/';
                localStorage.setItem('postLoginRedirect', path);
                localStorage.setItem('sessionExpired', '1');
            } catch {}
            expireSession();
            try { window.dispatchEvent(new Event('session-expired')); } catch {}
        }
    };

    const updateUserStatus = async (user_meta_data = {}) => {
        setUser((prev) => {
            if (!prev) return prev;
            const next = normalizeAuthBooleans({ ...prev, ...user_meta_data });
            if (user_meta_data.user_meta) {
                next.user_meta = {
                    ...(prev.user_meta || {}),
                    ...(user_meta_data.user_meta || {}),
                };
            }
            const firstName = user_meta_data.user_first_name ?? prev.user_first_name;
            const lastName = user_meta_data.user_last_name ?? prev.user_last_name;
            if (!user_meta_data.user_display_name && (user_meta_data.user_first_name !== undefined || user_meta_data.user_last_name !== undefined)) {
                next.user_display_name = `${firstName || ""} ${lastName || ""}`.trim() || prev.user_display_name;
            }
            try {
                localStorage.setItem("user", JSON.stringify(next));
            } catch (e) {
                console.error("Failed to persist user data", e);
            }
            return next;
        });
    };

    useEffect(() => {
        checkSessionExpiration();
        const interval = setInterval(checkSessionExpiration, 5 * 60 * 1000); // Check every 5 minutes
        // Listen for global session-expired events and expire auth immediately
        const handler = () => expireSession();
        window.addEventListener('session-expired', handler);
        return () => { clearInterval(interval); window.removeEventListener('session-expired', handler); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const normalizePermissionValue = (value) => {
        if (typeof value === "boolean") return value;
        if (typeof value === "number") return value === 1;
        if (typeof value === "string") {
            const normalized = value.trim().toLowerCase();
            return normalized === "true" || normalized === "1" || normalized === "yes";
        }
        return false;
    };

    const resolvePermissionValue = (path = "") => {
        if (!path) return undefined;
        const permissions = normalizePermissionsObject(user?.role?.permissions);
        if (!permissions || Object.keys(permissions).length === 0) return undefined;
        const keys = path.split(".");
        let current = permissions;
        for (const key of keys) {
            if (current?.[key] === undefined || current?.[key] === null) {
                return undefined;
            }
            current = current[key];
        }
        return current;
    };

    const hasPermission = (permissionPath) => {
        if (!permissionPath) return false;
        const direct = resolvePermissionValue(permissionPath);
        if (direct !== undefined) {
            return normalizePermissionValue(direct);
        }
        const aliases = PERMISSION_ALIASES[permissionPath] || [];
        for (const aliasPath of aliases) {
            const aliasValue = resolvePermissionValue(aliasPath);
            if (aliasValue !== undefined) {
                return normalizePermissionValue(aliasValue);
            }
        }
        return false;
    };

    const canViewProductPrice = () => hasPermission("order_management.view_product_price");
    const canAddToCart = () => hasPermission("order_management.add_to_cart");
    const canAddToRequestList = () => hasPermission("requests_management.add_to_request_list");

    const refreshRolePermissions = React.useCallback((updatedRole) => {
        if (!updatedRole?.role_id) return;
        setUser((prevUser) => {
            if (!prevUser?.role?.role_id) return prevUser;
            if (Number(prevUser.role.role_id) !== Number(updatedRole.role_id)) return prevUser;
            const nextRole = {
                ...prevUser.role,
                ...updatedRole,
                permissions: updatedRole.permissions || prevUser.role.permissions,
            };
            const updatedUser = { ...prevUser, role: nextRole };
            try {
                localStorage.setItem("user", JSON.stringify(updatedUser));
            } catch {}
            return updatedUser;
        });
    }, []);

    useEffect(() => {
        if (!user?.user_id) return;
        identifyUser(getAnalyticsUserPayload(user));
    }, [user]);
    const value = {
        user,
        loading,
        login,
        logout,
        expireSession,
        sendOtp,
        isAuthenticated: !!user,
        updateUserStatus,
        hasPermission,
        canViewProductPrice,
        canAddToCart,
        canAddToRequestList,
        refreshRolePermissions,
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export default AuthProvider;
